import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useChainsTheme } from '../../context/ChainsThemeContext';
import SystemInfo from '../SystemInfo/SystemInfo';
import { 
  BarChart3, ShoppingCart, Truck, Warehouse, TrendingUp, 
  FileText, Settings, Menu, X, Home, ChevronRight,
  Activity, Globe, MapPin, Package, Users, Clock,
  Bell, Search, Plus, Filter, Download, RefreshCw,
  Sun, Moon
} from 'lucide-react';

const ChainsLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState(5);
  const { isDarkMode, toggleDarkMode } = useChainsTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: BarChart3,
      path: '/chains',
      color: 'from-blue-500 to-cyan-500',
      description: 'Supply chain overview'
    },
    {
      id: 'purchase-requests',
      label: 'Purchase Requests',
      icon: ShoppingCart,
      path: '/chains/purchase-requests',
      color: 'from-emerald-500 to-teal-500',
      description: 'Create and track requests'
    },
    {
      id: 'logistics',
      label: 'Logistics',
      icon: Truck,
      path: '/chains/logistics',
      color: 'from-orange-500 to-red-500',
      description: 'Shipment tracking & routes'
    },
    {
      id: 'procurement',
      label: 'Procurement',
      icon: Package,
      path: '/chains/procurement',
      color: 'from-indigo-500 to-blue-500',
      description: 'Vendor management & POs'
    },
    {
      id: 'warehouse',
      label: 'Warehouse',
      icon: Warehouse,
      path: '/chains/warehouse',
      color: 'from-slate-500 to-gray-500',
      description: 'Inventory & storage'
    },
    {
      id: 'planning',
      label: 'Planning',
      icon: TrendingUp,
      path: '/chains/planning',
      color: 'from-green-500 to-emerald-500',
      description: 'Forecasting & analytics'
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: FileText,
      path: '/chains/reports',
      color: 'from-gray-500 to-slate-500',
      description: 'KPIs & insights'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      path: '/chains/settings',
      color: 'from-slate-500 to-gray-500',
      description: 'Configuration & users'
    }
  ];

  useEffect(() => {
    const path = location.pathname;
    const currentTab = navigationItems.find(item => path === item.path)?.id || 'dashboard';
    setActiveTab(currentTab);
  }, [location.pathname]);

  const Sidebar = () => (
    <motion.div
      initial={{ x: -300 }}
      animate={{ x: 0 }}
      exit={{ x: -300 }}
      className={`fixed left-0 top-0 h-full w-80 backdrop-blur-xl border-r z-50 ${
        isDarkMode 
          ? 'bg-black/90 border-white/10' 
          : 'bg-white/90 border-gray-200/50'
      }`}
    >
      {/* Logo Section */}
      <div className={`p-6 border-b ${
        isDarkMode ? 'border-white/10' : 'border-gray-200/50'
      }`}>
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
            <Globe className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Chains</h1>
            <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Supply Chain Command Center</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className={`p-4 border-b ${
        isDarkMode ? 'border-white/10' : 'border-gray-200/50'
      }`}>
        <div className="relative">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
            isDarkMode ? 'text-white/40' : 'text-gray-400'
          }`} />
          <input
            type="text"
            placeholder="Search chains..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full pl-10 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 ${
              isDarkMode 
                ? 'bg-white/5 border border-white/10 text-white placeholder-white/40' 
                : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-500'
            }`}
          />
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2">
        {navigationItems.map((item) => (
          <motion.button
            key={item.id}
            onClick={() => {
              navigate(item.path);
              setIsSidebarOpen(false);
            }}
            whileHover={{ x: 4 }}
            whileTap={{ scale: 0.98 }}
            className={`w-full flex items-center space-x-4 p-4 rounded-xl transition-all duration-300 ${
              activeTab === item.id
                ? 'bg-gradient-to-r ' + item.color + ' text-white shadow-lg'
                : isDarkMode 
                  ? 'text-white/70 hover:text-white hover:bg-white/5'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <div className={`p-2 rounded-lg ${
              activeTab === item.id ? 'bg-white/20' : isDarkMode ? 'bg-white/5' : 'bg-gray-100'
            }`}>
              <item.icon className="w-5 h-5" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium">{item.label}</p>
              <p className={`text-xs ${isDarkMode ? 'opacity-60' : 'text-gray-500'}`}>{item.description}</p>
            </div>
            <ChevronRight className={`w-4 h-4 ${isDarkMode ? 'opacity-40' : 'text-gray-400'}`} />
          </motion.button>
        ))}
      </nav>

      {/* Quick Actions */}
      <div className={`p-4 border-t ${
        isDarkMode ? 'border-white/10' : 'border-gray-200/50'
      }`}>
        <div className="grid grid-cols-2 gap-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`p-3 rounded-xl transition-all ${
              isDarkMode 
                ? 'bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10'
                : 'bg-gray-50 border border-gray-200 text-gray-700 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <Plus className="w-5 h-5 mx-auto mb-1" />
            <span className="text-xs">New Request</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`p-3 rounded-xl transition-all ${
              isDarkMode 
                ? 'bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10'
                : 'bg-gray-50 border border-gray-200 text-gray-700 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <Activity className="w-5 h-5 mx-auto mb-1" />
            <span className="text-xs">Live View</span>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className={`min-h-screen relative overflow-hidden ${
      isDarkMode 
        ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900' 
        : 'bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100'
    }`}>
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className={`absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl animate-pulse ${
          isDarkMode ? 'bg-blue-500/10' : 'bg-blue-500/20'
        }`}></div>
        <div className={`absolute top-1/2 right-0 w-96 h-96 rounded-full blur-3xl animate-pulse delay-1000 ${
          isDarkMode ? 'bg-cyan-500/10' : 'bg-cyan-500/20'
        }`}></div>
        <div className={`absolute bottom-0 left-1/2 w-96 h-96 rounded-full blur-3xl animate-pulse delay-2000 ${
          isDarkMode ? 'bg-slate-500/10' : 'bg-slate-500/20'
        }`}></div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className={`absolute w-2 h-2 rounded-full ${
              isDarkMode ? 'bg-white/20' : 'bg-gray-400/30'
            }`}
            animate={{
              x: [0, Math.random() * window.innerWidth],
              y: [0, Math.random() * window.innerHeight],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
            }}
          />
        ))}
      </div>

      {/* Top Bar */}
      <div className="relative z-10 flex items-center justify-between p-6">
        <div className="flex items-center space-x-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className={`p-3 backdrop-blur-sm border rounded-xl transition-all ${
              isDarkMode 
                ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                : 'bg-white/50 border-gray-200/50 text-gray-700 hover:bg-white/70'
            }`}
          >
            {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </motion.button>
          
          <div className="hidden md:block">
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Chains</h1>
            <p className={`${isDarkMode ? 'text-white/60' : 'text-gray-600'} text-sm`}>Supply Chain Intelligence Platform</p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Theme Toggle */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleDarkMode}
            className={`p-3 backdrop-blur-sm border rounded-xl transition-all ${
              isDarkMode 
                ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                : 'bg-white/50 border-gray-200/50 text-gray-700 hover:bg-white/70'
            }`}
          >
            {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </motion.button>

          {/* Search */}
          <div className="relative hidden md:block">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
              isDarkMode ? 'text-white/40' : 'text-gray-400'
            }`} />
            <input
              type="text"
              placeholder="Search supply chain..."
              className={`pl-10 pr-4 py-2 backdrop-blur-sm border rounded-xl w-64 focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
                isDarkMode 
                  ? 'bg-white/10 border-white/20 text-white placeholder-white/40'
                  : 'bg-white/50 border-gray-200/50 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

          {/* Notifications */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`relative p-3 backdrop-blur-sm border rounded-xl transition-all ${
              isDarkMode 
                ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                : 'bg-white/50 border-gray-200/50 text-gray-700 hover:bg-white/70'
            }`}
          >
            <Bell className="w-5 h-5" />
            {notifications > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold"
              >
                {notifications}
              </motion.span>
            )}
          </motion.button>

          {/* User Profile */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className={`flex items-center space-x-3 p-3 backdrop-blur-sm border rounded-xl ${
              isDarkMode 
                ? 'bg-white/10 border-white/20 text-white'
                : 'bg-white/50 border-gray-200/50 text-gray-700'
            }`}
          >
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center">
              <Users className="w-4 h-4 text-white" />
            </div>
            <div className="hidden md:block">
              <p className="text-sm font-medium">Supply Chain Manager</p>
              <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>Active</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Sidebar Overlay */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSidebarOpen(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            />
            <Sidebar />
          </>
        )}
      </AnimatePresence>

      {/* Main Content - Full Screen */}
      <div className="relative z-10 h-[calc(100vh-88px)] overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="h-full overflow-y-auto"
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* System Information Component */}
      <SystemInfo />
    </div>
  );
};

export default ChainsLayout; 